// Which tests are not passing, on which model, from which source.
//
// The header metrics answer "how many". They cannot answer "which", and a
// warning count with no name attached is a number nobody can act on: the
// operator still has to open EMR logs to find out that the 3 Silver warnings
// are orphan order_id references on fct_credit_memos, fct_invoices and
// fct_shipments, all from Magento.
//
// One dbt step runs per source per layer, so a full pipeline run produces four
// separate outcomes. Each gets its own chip, and the chips ARE the summary: a
// clean run renders them and nothing else, because a table of zero rows tells
// an operator less than one line saying every test passed.
//
// Two honesty rules carry over from the metrics header. Absent results render
// nothing at all, never an empty green table, because "nothing failed" and
// "nothing published yet" must never look the same. And the source column
// prints "unattributed" rather than guessing: attribution comes from the dbt
// step that ran the test, or from the lineage graph node, and when neither
// names one, saying so is better than picking.

import type { TestFailure, TestRun, TestScopeTally } from "../types";

// dbt severity. "warn" is a passing assertion with the severity turned down,
// so it is listed but never coloured like a failure.
const HARD = new Set(["fail", "error", "runtime error"]);

// Pipeline order, so the chips read the way the run actually happened rather
// than alphabetically. An unknown layer sorts last instead of being dropped.
const LAYER_ORDER = ["silver", "gold"];

function tone(status: string): "bad" | "warn" | "plain" {
  if (HARD.has(status)) return "bad";
  return status === "warn" ? "warn" : "plain";
}

// "silver/magento" -> ["silver", "magento"]. A bare layer carries no source.
function splitScope(scope: string): [string, string] {
  const [layer, source = ""] = scope.split("/");
  return [layer, source];
}

function titleCase(word: string): string {
  return word ? word[0].toUpperCase() + word.slice(1) : word;
}

// "silver/magento" -> "Magento Silver". The source leads because that is what
// an operator is triaging; a bare layer keeps its own name.
function scopeLabel(scope: string): string {
  const [layer, source] = splitScope(scope);
  return source ? `${titleCase(source)} ${titleCase(layer)}` : titleCase(layer);
}

function sourceLabel(row: TestFailure): string {
  return row.source_system || splitScope(row.scope ?? "")[1] || "unattributed";
}

function scopeSort(a: string, b: string): number {
  const [la, sa] = splitScope(a);
  const [lb, sb] = splitScope(b);
  const ia = LAYER_ORDER.indexOf(la);
  const ib = LAYER_ORDER.indexOf(lb);
  if (ia !== ib) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  return sa.localeCompare(sb);
}

function chipTone(t: TestScopeTally): "bad" | "warn" | "ok" {
  if (t.failed > 0) return "bad";
  return t.warned > 0 ? "warn" : "ok";
}

function chipText(t: TestScopeTally): string {
  if (t.failed > 0) return `${t.failed} failing`;
  if (t.warned > 0) return `${t.warned} warning${t.warned === 1 ? "" : "s"}`;
  return `${t.passed}/${t.total} passed`;
}

export function TestOutcomes({ run }: { run: TestRun }) {
  if (!run.available) return null;

  const rows = run.failures;
  const scopes = Object.entries(run.by_scope ?? {}).sort(([a], [b]) =>
    scopeSort(a, b),
  );
  const runCount = scopes.length || run.layers_present.length;

  return (
    <section className="to">
      <div className="to-head">
        <h3>Test outcomes</h3>
        <div className="to-scopes">
          {scopes.map(([scope, t]) => (
            <span key={scope} className={`to-chip to-${chipTone(t)}`}>
              <span className="to-chip-name">{scopeLabel(scope)}</span>
              {chipText(t)}
            </span>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="to-clear">
          All {run.total} tests passed across {runCount}{" "}
          {runCount === 1 ? "run" : "runs"}. Nothing to review.
        </p>
      ) : (
        <div className="to-wrap">
          <table className="to-table">
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Test</th>
                <th scope="col">Model</th>
                <th scope="col">Layer</th>
                <th scope="col">Source</th>
                <th scope="col" className="to-num">
                  Rows
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.scope ?? row.layer}:${row.test}`}>
                  <td>
                    <span className={`to-sev to-${tone(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="to-test" title={row.message || undefined}>
                    {row.test}
                  </td>
                  <td className="to-model">{row.model ?? "unmatched"}</td>
                  <td>{row.layer}</td>
                  <td>{sourceLabel(row)}</td>
                  <td className="to-num">
                    {row.failures === null ? "-" : row.failures.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {run.failures_truncated > 0 ? (
            <p className="to-more">{run.failures_truncated} more not shown.</p>
          ) : null}
        </div>
      )}

      {run.layers_missing.length > 0 ? (
        <p className="to-missing">
          No results published yet for{" "}
          {run.layers_missing.map(scopeLabel).join(", ")}.
        </p>
      ) : null}
    </section>
  );
}
